'use client';

import { MinimalTemplate } from '@payloadcms/next/templates';
import { Button, Form, Modal, SelectInput, toast, useConfig, useModal, useTranslation, XIcon } from '@payloadcms/ui';
import { BeforeListTableClientProps } from 'payload';
import React, { useCallback, useRef, useState } from 'react';
import { LlmKnowledgeBaseCreationMode } from '../../api/consts';
import { AppToolOption, LlmApp, LlmKnowledgeBasePollResponse } from '../../api/types';
import './GenerateKbModal.scss';

const baseClass = 'generate-kb-options';

type SelectOption = {
  label: string;
  value: string;
};

type BulkUploadResultItem = {
  filename: string;
  success: boolean;
  id?: string;
  error?: string;
};

type BulkUploadResponse = {
  results: BulkUploadResultItem[];
  totalFiles: number;
  successCount: number;
  errorCount: number;
};

export const BtnGenerateKbVectorDb: React.FC<BeforeListTableClientProps> = (props) => {
  const { config } = useConfig();
  const modalKey = 'generate-kb-modal';
  const bulkUploadModalKey = 'bulk-upload-modal';
  const { openModal, isModalOpen, closeAllModals } = useModal();
  const [loading, setLoading] = React.useState(false);

  // tools
  const [toolOptions, setToolOptions] = React.useState<SelectOption[]>([]);
  const [selectedTool, setSelectedTool] = React.useState('');
  // generation mode
  const [generationModeOptions, setGenerationModeOptions] = React.useState<
    SelectOption[]
  >([]);
  const [selectedGenerationMode, setSelectedGenerationMode] =
    React.useState('');
  // llm apps
  const [llmApps, setllmApps] = useState<SelectOption[]>([]);
  const [selectedLlmApp, setSelectedLlmApp] = React.useState('');

  // bulk upload state
  const [bulkLlmApps, setBulkLlmApps] = useState<SelectOption[]>([]);
  const [bulkSelectedApp, setBulkSelectedApp] = useState('');
  const [bulkToolOptions, setBulkToolOptions] = useState<SelectOption[]>([]);
  const [bulkSelectedTool, setBulkSelectedTool] = useState('');
  const [bulkFiles, setBulkFiles] = useState<File[]>([]);
  const [bulkUploading, setBulkUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { t } = useTranslation<any, any>();
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const { routes: { api: apiRoute } = {} } = config;

  type LlmKbGenerationResponse = {
    traceId: string;
  };

  React.useEffect(() => {
    const fetchOptions = async () => {
      try {
        if (!selectedLlmApp) {
          return;
        }

        const url = `${process.env.PAYLOAD_PUBLIC_BASE_PATH || ''}/api/llm/appTools?llmApp=${selectedLlmApp}&onlyActive`;
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          // tools select options
          const toolsOpts = (await response.json()) as AppToolOption[];
          setToolOptions(toolsOpts);
          if (toolsOpts.length > 0) {
            setSelectedTool('');
          }

          // generation mode select options
          const generationModeOpts: SelectOption[] = [
            {
              label: t('general:generateKbAppOnly'),
              value: LlmKnowledgeBaseCreationMode.appOnly,
            },
          ];
          if (toolsOpts.length > 0) {
            generationModeOpts.unshift({
              label: t('general:generateKbAppAndTools'),
              value: LlmKnowledgeBaseCreationMode.appAndTools,
            });
            generationModeOpts.push({
              label: t('general:generateKbToolsOnly'),
              value: LlmKnowledgeBaseCreationMode.toolsOnly,
            });
            generationModeOpts.push({
              label: t('general:generateKbSpecificTool'),
              value: LlmKnowledgeBaseCreationMode.specificTool,
            });
          }
          setGenerationModeOptions(generationModeOpts);
          setSelectedGenerationMode(generationModeOpts[0]!.value);
        } else {
          setToolOptions([
            { label: (await response.json()).message, value: '' },
          ]);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLlmApp]);

  // Fetch tool options for bulk upload when app changes
  React.useEffect(() => {
    const fetchBulkToolOptions = async () => {
      try {
        if (!bulkSelectedApp) {
          setBulkToolOptions([]);
          return;
        }

        const url = `${process.env.PAYLOAD_PUBLIC_BASE_PATH || ''}/api/llm/appTools?llmApp=${bulkSelectedApp}&onlyActive`;
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          const toolsOpts = (await response.json()) as AppToolOption[];
          setBulkToolOptions([
            { label: t('general:kbFileNoToolOption'), value: '' },
            ...toolsOpts,
          ]);
        }
      } catch (error) {
        console.error('Error fetching bulk tool options:', error);
      }
    };

    fetchBulkToolOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bulkSelectedApp]);

  const pollKbGeneration = useCallback(async (traceId: string) => {
    try {
      console.log('polling job ...', traceId);

      const httpResponse = await fetch(`${apiRoute}/llm/kb/${traceId}/poll`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (httpResponse.ok) {
        const response =
          (await httpResponse.json()) as LlmKnowledgeBasePollResponse;
        console.log(response);

        if (!response.processing) {
          if (response.success) {
            toast.success(t('general:generateKbSuccess') as string);
          } else {
            const errors = response.errors || [];

            const toastMsg = (
              <div>
                <div>{t('general:generateKbError')}</div>
                <ul>
                  {errors.map((error, idx) => (
                    <li key={idx}>
                      <div>
                        {error.job}: {error.taskStatus} - {error.error}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            );
            toast.error(toastMsg, {}); // { autoClose: false });
          }

          setLoading(false);
          clearInterval(intervalRef.current as ReturnType<typeof setInterval>);
        }
      } else {
        throw new Error(`Response status: ${httpResponse.status}`);
      }
    } catch (error: unknown) {
      clearInterval(intervalRef.current as ReturnType<typeof setInterval>);
      console.error(`${t('general:generateKbError')}: ${error}`);
      toast.error(`${t('general:generateKbError')}: ${error}`, {}); // { autoClose: false });
      setLoading(false);
    }
  }, [apiRoute, t]);

  const generateKb = async () => {
    setLoading(true);

    try {
      const httpResponse = await fetch(`${apiRoute}/llm/kb`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        referrer: '',
        body: JSON.stringify({
          appId: selectedLlmApp,
          mode: selectedGenerationMode,
          toolFunctionId: selectedTool,
        }),
      });

      if (httpResponse.ok) {
        const response = (await httpResponse.json()) as LlmKbGenerationResponse;
        const intervalId = setInterval(() => {
          pollKbGeneration(response.traceId);
        }, 2000);
        intervalRef.current = intervalId;
      } else {
        throw new Error(`Response status: ${httpResponse.status}`);
      }
    } catch (error: unknown) {
      console.error(`${t('general:generateKbError')}: ${error}`);
      toast.error(`${t('general:generateKbError')}: ${error}`, {}); // { autoClose: false });
      setLoading(false);
    }
  };

  const onSubmit = async () => {
    try {
      // generate kb with the selected options
      generateKb();
      closeAllModals();
    } catch (error) {
      console.error(t('general:generateKbError') + ': ', error);
      toast.error(t('general:generateKbError') as string);
    }
  };

  const handleClick = async () => {
    try {
      // get user apps for which it's possible to generate kb
      const httpResponse = await fetch(`${apiRoute}/llm/apps`, {
        method: 'GET',
      });
      const response = await httpResponse.json();
      if (httpResponse.ok) {
        const apps: LlmApp[] = response;
        setllmApps(apps.map((app) => ({ value: app.id, label: app.name })));
        openModal(modalKey);
      } else {
        toast.error(response.message);
        return;
      }
    } catch (error: unknown) {
      console.error(t('general:generateKbError') + ': ', error);
      toast.error(t('general:generateKbError') as string);
    }
  };

  // --- Bulk Upload handlers ---

  const handleBulkUploadClick = async () => {
    try {
      const httpResponse = await fetch(`${apiRoute}/llm/apps`, {
        method: 'GET',
      });
      const response = await httpResponse.json();
      if (httpResponse.ok) {
        const apps: LlmApp[] = response;
        setBulkLlmApps(apps.map((app) => ({ value: app.id, label: app.name })));
        setBulkSelectedApp('');
        setBulkSelectedTool('');
        setBulkFiles([]);
        openModal(bulkUploadModalKey);
      } else {
        toast.error(response.message);
      }
    } catch (error: unknown) {
      console.error(`${t('general:bulkUploadError')}: ${error}`);
      toast.error(t('general:bulkUploadError') as string);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      setBulkFiles((prev) => [...prev, ...newFiles]);
    }
    // reset input so the same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeFile = (index: number) => {
    setBulkFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleBulkUploadSubmit = async () => {
    if (!bulkSelectedApp || bulkFiles.length === 0) return;

    setBulkUploading(true);

    try {
      const formData = new FormData();
      formData.append('llmApp', bulkSelectedApp);
      if (bulkSelectedTool) {
        formData.append('appTool', bulkSelectedTool);
      }
      bulkFiles.forEach((file) => {
        formData.append('files', file, file.name);
      });

      const httpResponse = await fetch(`${apiRoute}/llm/kb/bulk-upload`, {
        method: 'POST',
        body: formData,
      });

      if (httpResponse.ok || httpResponse.status === 207) {
        const result = (await httpResponse.json()) as BulkUploadResponse;
        if (result.errorCount === 0) {
          toast.success(
            `${t('general:bulkUploadSuccess')} (${result.successCount} file)`
          );
        } else {
          const toastMsg = (
            <div>
              <div>{t('general:bulkUploadPartial')}</div>
              <div>✓ {result.successCount} | ✗ {result.errorCount}</div>
              <ul>
                {result.results
                  .filter((r: BulkUploadResultItem) => !r.success)
                  .map((r: BulkUploadResultItem, i: number) => (
                    <li key={i}>{r.filename}: {r.error}</li>
                  ))}
              </ul>
            </div>
          );
          toast.warning(toastMsg, {}); // { autoClose: false });
        }
        closeAllModals();
        // reload the page to show new files
        window.location.reload();
      } else {
        throw new Error(`Response status: ${httpResponse.status}`);
      }
    } catch (error: unknown) {
      console.error(`${t('general:bulkUploadError')}: ${error}`);
      toast.error(`${t('general:bulkUploadError')}: ${error}`, {}); // { autoClose: false });
    } finally {
      setBulkUploading(false);
    }
  };

  const handleSelectChange = (setter: (val: string) => void) => {
    return (e: { value: unknown }) => setter(e.value as string);
  };

  return (
    <div>
      <div className={`${baseClass}__bulk-upload-header`}>
        <Button onClick={handleBulkUploadClick} buttonStyle="secondary" disabled={bulkUploading}>
          {bulkUploading ? t('general:bulkUploadUploading') : t('general:bulkUpload')}
        </Button>
      </div>
      <Button onClick={handleClick} disabled={loading}>
        {loading ? t('general:generatingKb') : t('general:generateKb')}
      </Button>

      {/* Generate KB Modal */}
      <Modal slug={modalKey} className={`${baseClass}__modal`}>
        {isModalOpen(modalKey) && (
          <MinimalTemplate>
            <header className={`${baseClass}__header`}>
              <h3>{t('general:generateKbModalTitle')}</h3>
              <Button buttonStyle="none" onClick={closeAllModals}>
                <XIcon />
              </Button>
            </header>
            <main className={`${baseClass}__main`}>
              <Form onSubmit={onSubmit}>
                <div className="render-fields">
                  <SelectInput
                    label={t('general:selectLlmApp')}
                    name="llmApp"
                    path={'llmApp'}
                    options={llmApps}
                    value={selectedLlmApp}
                    onChange={(option) => setSelectedLlmApp(
                      String(Array.isArray(option) ? option.map(x => x.value).join(', ') : option.value)
                    )}
                  />
                  <SelectInput
                    label={t('general:selectKbGenerationMode')}
                    name="generationMode"
                    path={'generationMode'}
                    options={generationModeOptions}
                    value={selectedGenerationMode}
                    onChange={(option) => setSelectedGenerationMode(
                      String(Array.isArray(option) ? option.map(x => x.value).join(', ') : option.value)
                    )}
                  />
                  {selectedGenerationMode === LlmKnowledgeBaseCreationMode.specificTool &&
                    (
                      <SelectInput
                        label={t('general:selectTool')}
                        name="tool"
                        path={'tool'}
                        options={toolOptions}
                        value={selectedTool}
                        onChange={(option) => setSelectedTool(
                          String(Array.isArray(option) ? option.map(x => x.value).join(', ') : option.value)
                        )}
                      />
                    )
                  }

                </div>
                <div className={`${baseClass}__foot`}>
                  <Button type="submit">{t('general:submit')}</Button>
                </div>
              </Form>
            </main>
          </MinimalTemplate>
        )}
      </Modal>

      {/* Bulk Upload Modal */}
      <Modal slug={bulkUploadModalKey} className={`${baseClass}__modal`}>
        {isModalOpen(bulkUploadModalKey) && (
          <MinimalTemplate>
            <header className={`${baseClass}__header`}>
              <h3>{t('general:bulkUploadTitle')}</h3>
              <Button buttonStyle="none" onClick={closeAllModals}>
                <XIcon />
              </Button>
            </header>
            <main className={`${baseClass}__main`}>
              <div className="render-fields">
                <SelectInput
                  label={t('general:selectLlmApp')}
                  name="bulkLlmApp"
                  path={'bulkLlmApp'}
                  options={bulkLlmApps}
                  value={bulkSelectedApp}
                  onChange={(option) => setBulkSelectedApp(
                    String(Array.isArray(option) ? option.map(x => x.value).join(', ') : option.value)
                  )}
                />
                {bulkToolOptions.length > 0 && (
                  <SelectInput
                    label={t('general:selectTool')}
                    name="bulkTool"
                    path={'bulkTool'}
                    options={bulkToolOptions}
                    value={bulkSelectedTool}
                    onChange={(option) => setBulkSelectedTool(
                      String(Array.isArray(option) ? option.map(x => x.value).join(', ') : option.value)
                    )}
                  />
                )}

                <div className={`${baseClass}__dropzone`}>
                  <label className={`${baseClass}__dropzone-label`}>
                    {t('general:selectFiles')}
                  </label>
                  <div
                    className={`${baseClass}__dropzone-area`}
                    onClick={() => fileInputRef.current?.click()}
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.currentTarget.classList.add(`${baseClass}__dropzone-area--active`);
                    }}
                    onDragLeave={(e) => {
                      e.currentTarget.classList.remove(`${baseClass}__dropzone-area--active`);
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      e.currentTarget.classList.remove(`${baseClass}__dropzone-area--active`);
                      if (e.dataTransfer.files) {
                        const newFiles = Array.from(e.dataTransfer.files);
                        setBulkFiles((prev) => [...prev, ...newFiles]);
                      }
                    }}
                  >
                    <span className={`${baseClass}__dropzone-icon`}>📁</span>
                    <span className={`${baseClass}__dropzone-text`}>
                      {bulkFiles.length === 0
                        ? t('general:noFilesSelected')
                        : `${bulkFiles.length} ${t('general:filesSelected')}`}
                    </span>
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    style={{ display: 'none' }}
                    onChange={handleFileSelect}
                  />
                </div>

                {bulkFiles.length > 0 && (
                  <div className={`${baseClass}__file-list`}>
                    {bulkFiles.map((file, index) => (
                      <div key={`${file.name}-${index}`} className={`${baseClass}__file-item`}>
                        <span className={`${baseClass}__file-name`}>{file.name}</span>
                        <span className={`${baseClass}__file-size`}>
                          {(file.size / 1024).toFixed(1)} KB
                        </span>
                        <Button
                          buttonStyle="none"
                          className={`${baseClass}__file-remove`}
                          onClick={() => removeFile(index)}
                        >
                          <XIcon />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className={`${baseClass}__foot`}>
                <Button
                  onClick={handleBulkUploadSubmit}
                  disabled={!bulkSelectedApp || bulkFiles.length === 0 || bulkUploading}
                >
                  {bulkUploading
                    ? t('general:bulkUploadUploading')
                    : `${t('general:bulkUpload')} (${bulkFiles.length})`}
                </Button>
              </div>
            </main>
          </MinimalTemplate>
        )}
      </Modal>
    </div>
  );
};
